import React from 'react';
import './GWRecapPost.css';

// Difficulty color map matching existing card.css palette
const difficultyColors = {
  1: 'rgb(159, 251, 159)',
  2: 'rgb(152, 241, 130)',
  3: 'rgb(162, 236, 143)',
  4: 'rgb(187, 219, 132)',
  5: 'rgb(223, 237, 153)',
  6: 'rgb(245, 245, 137)',
  7: 'rgb(250, 214, 143)',
  8: 'rgb(245, 189, 137)',
  9: 'rgb(240, 164, 164)',
  10: 'rgb(243, 143, 143)'
};

function getDifficultyColor(value) {
  const rounded = Math.min(10, Math.max(1, Math.round(value)));
  return difficultyColors[rounded] || difficultyColors[5];
}

/**
 * Determines the primary reasons for an FDR change by comparing individual metric deltas.
 * Each metric is evaluated independently for more specific reason text.
 */
function generateReason(current, previous, direction) {
  const factors = [];
  const up = direction === 'up';

  // Helper: only compare when both snapshots have real data for a field
  const hasBoth = (field) => current[field] != null && previous[field] != null && current[field] !== 0 && previous[field] !== 0;
  const delta = (field) => (current[field] || 0) - (previous[field] || 0);

  // Each factor's wording must follow its OWN delta's sign, not the team's
  // overall combined direction — a team can rise overall while one factor
  // (e.g. away PPG) individually fell, and the text needs to say so.

  // Overall form
  const formDelta = hasBoth('recent_form_score') ? delta('recent_form_score') : 0;
  if (Math.abs(formDelta) > 0.1) {
    factors.push({
      magnitude: Math.abs(formDelta),
      text: formDelta > 0 ? 'Improved overall form' : 'Declining overall form'
    });
  }

  // Goals scored per 90 — home
  const gsHomeDelta = hasBoth('home_goals_scored_per_90') ? delta('home_goals_scored_per_90') : 0;
  if (Math.abs(gsHomeDelta) > 0.05) {
    factors.push({
      magnitude: Math.abs(gsHomeDelta) * 2.5,
      text: gsHomeDelta > 0 ? 'Scoring more goals at home' : 'Scoring less goals at home'
    });
  }

  // Goals scored per 90 — away
  const gsAwayDelta = hasBoth('away_goals_scored_per_90') ? delta('away_goals_scored_per_90') : 0;
  if (Math.abs(gsAwayDelta) > 0.05) {
    factors.push({
      magnitude: Math.abs(gsAwayDelta) * 2.5,
      text: gsAwayDelta > 0 ? 'Scoring more goals away' : 'Scoring less goals away'
    });
  }

  // Goals conceded per 90 — home
  const gcHomeDelta = hasBoth('home_goals_conceded_per_90') ? delta('home_goals_conceded_per_90') : 0;
  if (Math.abs(gcHomeDelta) > 0.05) {
    factors.push({
      magnitude: Math.abs(gcHomeDelta) * 3,
      text: gcHomeDelta < 0 ? 'Conceding less goals at home' : 'Conceding more goals at home'
    });
  }

  // Goals conceded per 90 — away
  const gcAwayDelta = hasBoth('away_goals_conceded_per_90') ? delta('away_goals_conceded_per_90') : 0;
  if (Math.abs(gcAwayDelta) > 0.05) {
    factors.push({
      magnitude: Math.abs(gcAwayDelta) * 3,
      text: gcAwayDelta < 0 ? 'Conceding less goals away' : 'Conceding more goals away'
    });
  }

  // xG per 90 — home
  const xgHomeDelta = hasBoth('home_xg_per_90') ? delta('home_xg_per_90') : 0;
  if (Math.abs(xgHomeDelta) > 0.05) {
    factors.push({
      magnitude: Math.abs(xgHomeDelta) * 2,
      text: xgHomeDelta > 0 ? 'Creating higher home xG' : 'Creating lower home xG'
    });
  }

  // xG per 90 — away
  const xgAwayDelta = hasBoth('away_xg_per_90') ? delta('away_xg_per_90') : 0;
  if (Math.abs(xgAwayDelta) > 0.05) {
    factors.push({
      magnitude: Math.abs(xgAwayDelta) * 2,
      text: xgAwayDelta > 0 ? 'Creating higher away xG' : 'Creating lower away xG'
    });
  }

  // xGC per 90 — home
  const xgcHomeDelta = hasBoth('home_xgc_per_90') ? delta('home_xgc_per_90') : 0;
  if (Math.abs(xgcHomeDelta) > 0.05) {
    factors.push({
      magnitude: Math.abs(xgcHomeDelta) * 2,
      text: xgcHomeDelta < 0 ? 'Conceding lower home xGC' : 'Conceding higher home xGC'
    });
  }

  // xGC per 90 — away
  const xgcAwayDelta = hasBoth('away_xgc_per_90') ? delta('away_xgc_per_90') : 0;
  if (Math.abs(xgcAwayDelta) > 0.05) {
    factors.push({
      magnitude: Math.abs(xgcAwayDelta) * 2,
      text: xgcAwayDelta < 0 ? 'Conceding lower away xGC' : 'Conceding higher away xGC'
    });
  }

  // Home PPG (Last 5) — full text on desktop, "(last 5)" dropped on mobile
  const homePpgDelta = hasBoth('home_ppg_recent_score') ? delta('home_ppg_recent_score') : 0;
  if (Math.abs(homePpgDelta) > 0.1) {
    factors.push({
      magnitude: Math.abs(homePpgDelta) * 1.5,
      text: homePpgDelta > 0 ? 'Increasing home PPG (last 5)' : 'Decreasing home PPG (last 5)',
      mobileText: homePpgDelta > 0 ? 'Increasing home PPG' : 'Decreasing home PPG'
    });
  }

  // Away PPG (Last 5) — full text on desktop, "(last 5)" dropped on mobile
  const awayPpgDelta = hasBoth('away_ppg_recent_score') ? delta('away_ppg_recent_score') : 0;
  if (Math.abs(awayPpgDelta) > 0.1) {
    factors.push({
      magnitude: Math.abs(awayPpgDelta) * 1.5,
      text: awayPpgDelta > 0 ? 'Better away PPG (last 5)' : 'Worse away PPG (last 5)',
      mobileText: awayPpgDelta > 0 ? 'Better away PPG' : 'Worse away PPG'
    });
  }

  // Sort by magnitude and pick top 2 reasons
  factors.sort((a, b) => b.magnitude - a.magnitude);
  if (factors.length === 0) {
    // No individual factor cleared its threshold — fall back to the team's
    // overall combined direction since there's nothing more specific to say.
    const fallback = up ? 'Small improvements across metrics' : 'Small declines across metrics';
    return [{ text: fallback, mobileText: fallback }];
  }
  return factors.slice(0, 2).map(f => ({ text: f.text, mobileText: f.mobileText || f.text }));
}

/**
 * GWRecapPost - Displays FDR movers (top 3 risers and top 3 fallers) for a gameweek.
 *
 * Props:
 *  - currentSnapshots: array of team snapshots for the current GW
 *  - previousSnapshots: array of team snapshots for the previous GW
 *  - gameweekName: string (e.g. "Gameweek 12")
 *  - teams: teams array from dummy.js (for badges and names)
 *  - isLight: boolean for alternating background
 *  - isMidSeason: boolean for mid-season update rendering
 *  - gameweeksElapsed: number of GWs between baseline and current
 *  - baselineGWName: name of baseline gameweek (e.g. "Gameweek 8")
 *  - currentGWName: name of current gameweek (e.g. "Gameweek 12")
 *  - nextFixtureDate: ISO date string of next PL fixture
 *  - createdAt: ISO date string when mid-season update was created
 *  - fixturesData: array of FPL fixture objects (for shock results and big wins)
 */
const GWRecapPost = ({ currentSnapshots, previousSnapshots, gameweekName, lastKickoff, teams, isLight, isLive, matchesPlayed, updatedAt, isMidSeason, gameweeksElapsed, baselineGWName, currentGWName, nextFixtureDate, createdAt, fixturesData, matches }) => {
  // Build lookup maps by team_id
  const currMap = {};
  currentSnapshots.forEach(s => { currMap[s.team_id] = s; });
  const prevMap = {};
  previousSnapshots.forEach(s => { prevMap[s.team_id] = s; });

  // Newly promoted teams have no snapshot from a season they weren't in the
  // top flight for, so they'd otherwise be silently excluded from every
  // riser/faller/shock comparison even once they have real current-season
  // data. Give them a synthetic "previous" entry at the season-start baseline
  // (3.0 home / 2.0 away) so their actual movement from that point still shows.
  Object.keys(currMap).forEach(teamId => {
    const tid = parseInt(teamId);
    if (!prevMap[tid]) {
      prevMap[tid] = { team_id: tid, home_difficulty: 3.0, away_difficulty: 2.0 };
    }
  });

  // Compute total difficulty change for each team present in both snapshots
  const movers = [];
  Object.keys(currMap).forEach(teamId => {
    const tid = parseInt(teamId);
    const curr = currMap[tid];
    const prev = prevMap[tid];
    if (!curr || !prev) return;

    // Round to 1dp first so displayed values and change are consistent
    const round1 = (v) => Math.round(parseFloat(v) * 10) / 10;
    const currHome = round1(curr.home_difficulty);
    const currAway = round1(curr.away_difficulty);
    const prevHome = round1(prev.home_difficulty);
    const prevAway = round1(prev.away_difficulty);
    const change = (currHome + currAway) - (prevHome + prevAway);

    movers.push({
      team_id: tid,
      change,
      homeChange: currHome - prevHome,
      awayChange: currAway - prevAway,
      currHome,
      currAway,
      prevHome,
      prevAway,
      current: curr,
      previous: prev
    });
  });

  // Sort: risers (biggest positive change first), fallers (biggest negative change first)
  const risers = movers
    .filter(m => m.change > 0)
    .sort((a, b) => b.change - a.change)
    .slice(0, 3);

  const fallers = movers
    .filter(m => m.change < 0)
    .sort((a, b) => a.change - b.change)
    .slice(0, 3);

  // Don't render if there are no movers at all
  if (risers.length === 0 && fallers.length === 0) return null;

  const getTeam = (teamId) => teams.find(t => t.id === teamId) || { name: 'Unknown', badge: null, initial: '???' };

  const formatChange = (val) => {
    const sign = val > 0 ? '+' : '';
    return `${sign}${val.toFixed(1)}`;
  };

  const renderMoverCard = (mover, direction) => {
    const team = getTeam(mover.team_id);
    const reasons = generateReason(mover.current, mover.previous, direction);
    const totalFDR = (mover.currHome + mover.currAway) / 2;

    return (
      <div key={mover.team_id} className={`mover-card mover-${direction}`}>
        <div
          className="mover-total-fdr"
          style={{ backgroundColor: getDifficultyColor(totalFDR) }}
          title="Total FDR (average of home and away)"
        >
          <span className="mover-total-fdr-value">{totalFDR.toFixed(1)}</span>
          <span className="mover-total-fdr-label">FDR</span>
        </div>
        <div className="mover-card-header">
          {team.badge && (
            <img src={team.badge} alt={team.name} className="mover-badge" />
          )}
          <div className="mover-card-info">
            <span className="mover-team-name">{team.name}</span>
            <span className={`mover-change mover-change-${direction}`}>
              {direction === 'up' ? '\u25B2' : '\u25BC'} {formatChange(mover.change)}
            </span>
          </div>
        </div>
        <div className="mover-details">
          <div className="mover-venue-block">
            <span className="mover-venue-label">H:</span>
            <div className="mover-venue-values">
              <span
                className="mover-difficulty-badge"
                style={{ backgroundColor: getDifficultyColor(mover.prevHome) }}
              >
                {mover.prevHome.toFixed(1)}
              </span>
              <span className="mover-arrow">{'\u2192'}</span>
              <span
                className="mover-difficulty-badge"
                style={{ backgroundColor: getDifficultyColor(mover.currHome) }}
              >
                {mover.currHome.toFixed(1)}
              </span>
            </div>
          </div>
          <div className="mover-venue-block">
            <span className="mover-venue-label">A:</span>
            <div className="mover-venue-values">
              <span
                className="mover-difficulty-badge"
                style={{ backgroundColor: getDifficultyColor(mover.prevAway) }}
              >
                {mover.prevAway.toFixed(1)}
              </span>
              <span className="mover-arrow">{'\u2192'}</span>
              <span
                className="mover-difficulty-badge"
                style={{ backgroundColor: getDifficultyColor(mover.currAway) }}
              >
                {mover.currAway.toFixed(1)}
              </span>
            </div>
          </div>
        </div>
        <ul className="mover-reasons">
          {reasons.map((r, i) => (
            <li key={i} className="mover-reason">
              <span className="reason-desktop">{r.text}</span>
              <span className="reason-mobile">{r.mobileText}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Extract GW number from name (e.g. "Gameweek 12" → "12")
  const gwNumber = gameweekName ? gameweekName.replace(/\D/g, '') : '';
  const gwEvent = gwNumber ? parseInt(gwNumber) : null;

  // Treat this GW as no longer live once every one of its fixtures has
  // actually finished (finished_provisional flips right after the final
  // whistle), rather than trusting the isLive prop alone — that stays true
  // until the next gameweek's snapshot exists, a gap of hours/days after the
  // GW itself is over.
  const gwEventFixtures = gwEvent && fixturesData ? fixturesData.filter(f => f.event === gwEvent) : [];
  const stillLive = isLive && (gwEventFixtures.length === 0 || gwEventFixtures.some(f => !f.finished_provisional));

  // Format date string depending on live vs finished vs mid-season state
  const dateDisplay = (() => {
    if (isMidSeason && createdAt) {
      const date = new Date(createdAt);
      return `Posted: ${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`;
    }
    if (stillLive && updatedAt) {
      const date = new Date(updatedAt);
      const dateStr = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      const timeStr = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      return `Last updated: ${dateStr} at ${timeStr}`;
    }
    const date = lastKickoff ? new Date(lastKickoff) : new Date();
    return `Posted: ${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`;
  })();

  // Build mid-season summary description
  const buildMidSeasonDescription = () => {
    const nextDateStr = nextFixtureDate
      ? new Date(nextFixtureDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
      : 'TBC';

    let desc = `There are no Premier League matches this weekend, so time for a mid-season update!`;
    const baselineNum = baselineGWName ? baselineGWName.replace(/\D/g, '') : '';
    const currentNum = currentGWName ? currentGWName.replace(/\D/g, '') : '';
    desc += ` Over the last ${gameweeksElapsed} gameweek${gameweeksElapsed !== 1 ? 's' : ''} (GW${baselineNum}-${currentNum})`;

    // Helper to lowercase reason but preserve acronyms like PPG, xG, xGC
    const lowerReason = (r) => r.charAt(0).toLowerCase() + r.slice(1);

    if (risers.length > 0) {
      const topRiser = risers[0];
      const team = getTeam(topRiser.team_id);
      const reasons = generateReason(topRiser.current, topRiser.previous, 'up');
      desc += ` the biggest riser has been ${team.name} (${formatChange(topRiser.change)}) due to their ${lowerReason(reasons[0].text)}`;
    }
    if (fallers.length > 0) {
      const topFaller = fallers[0];
      const team = getTeam(topFaller.team_id);
      const reasons = generateReason(topFaller.current, topFaller.previous, 'down');
      desc += ` and the biggest faller has been ${team.name} (${formatChange(topFaller.change)}) because of their ${lowerReason(reasons[0].text)}.`;
    }
    desc += ` Check out the other major changes in form for teams below. The next GW deadline is ${nextDateStr}, so enjoy the break before then and plan your transfers well! `;

    return desc;
  };

  const description = isMidSeason
    ? buildMidSeasonDescription()
    : stillLive
      ? `Here are the biggest FDR changes during Gameweek ${gwNumber} (${matchesPlayed}/10 matches played). Our FDR ratings are automatically recalculated after every match, factoring in goals scored and conceded per 90, expected goals (xG and xGC), points per game over the last 5 home and away matches, and overall recent form — giving you the most up-to-date picture of team strength.`
      : `Here are the biggest FDR changes following Gameweek ${gwNumber}. Our FDR ratings are automatically recalculated after every match, factoring in goals scored and conceded per 90, expected goals (xG and xGC), points per game over the last 5 home and away matches, and overall recent form - giving the most up-to-date picture of team strength.`;

  const title = isMidSeason ? 'Mid-Season FDR Update' : `GW${gwNumber} FDR Review`;

  // Analyse fixtures for this GW: biggest shock and big wins (3+ goals)
  // For finished GWs: prefer FPL API fixturesData (authoritative, immune to post-season player transfers
  // corrupting the player_gameweek_stats team_id join). For live GWs: prefer Supabase matches (more
  // up-to-date mid-GW) and fall back to FPL API for already-finished matches.
  const gwFixtures = !isMidSeason
    ? (() => {
        if (!gwEvent) return [];
        const apiMatches = fixturesData
          ? fixturesData.filter(f => f.event === gwEvent && f.finished && f.team_h_score != null)
          : [];
        if (!isLive) {
          return apiMatches.length > 0 ? apiMatches : (matches || []);
        } else {
          return matches && matches.length > 0 ? matches : apiMatches;
        }
      })()
    : [];

  let biggestShock = null;
  const bigWins = [];

  if (gwFixtures.length > 0) {
    gwFixtures.forEach(f => {
      const margin = Math.abs(f.team_h_score - f.team_a_score);
      const homeWon = f.team_h_score > f.team_a_score;

      // Big wins: 3+ goal margin OR winner scored 4+ goals
      const winnerScore = homeWon ? f.team_h_score : f.team_a_score;
      if (margin >= 3 || (margin > 0 && winnerScore >= 4)) {
        const winnerId = homeWon ? f.team_h : f.team_a;
        const loserId = homeWon ? f.team_a : f.team_h;
        const winnerTeam = getTeam(winnerId);
        const loserTeam = getTeam(loserId);
        bigWins.push({
          winner: winnerTeam.name,
          loser: loserTeam.name,
          score: homeWon ? `${f.team_h_score}-${f.team_a_score}` : `${f.team_a_score}-${f.team_h_score}`,
          margin,
          winnerBadge: winnerTeam.badge
        });
      }

      // Shock result: weaker FDR team (lower difficulty = easier opponent) beats stronger team
      if (margin > 0) {
        const winnerId = homeWon ? f.team_h : f.team_a;
        const loserId = homeWon ? f.team_a : f.team_h;
        const winnerPrev = prevMap[winnerId];
        const loserPrev = prevMap[loserId];
        if (winnerPrev && loserPrev) {
          const winnerFDR = homeWon
            ? (winnerPrev.home_difficulty || 5)
            : (winnerPrev.away_difficulty || 5);
          const loserFDR = homeWon
            ? (loserPrev.away_difficulty || 5)
            : (loserPrev.home_difficulty || 5);
          const fdrGap = loserFDR - winnerFDR;
          const compareScore = fdrGap; // wins have no handicap
          if (fdrGap > 0 && (!biggestShock || compareScore > biggestShock.compareScore || (compareScore === biggestShock.compareScore && margin > biggestShock.margin))) {
            const winnerTeam = getTeam(winnerId);
            const loserTeam = getTeam(loserId);
            biggestShock = {
              winner: winnerTeam.name,
              loser: loserTeam.name,
              score: `${f.team_h_score}-${f.team_a_score}`,
              fdrGap,
              compareScore,
              margin,
              winnerBadge: winnerTeam.badge,
              winnerFDR: winnerFDR.toFixed(1),
              loserFDR: loserFDR.toFixed(1),
              homeWon,
              isDraw: false
            };
          }
        }
      }

      // Draw shock: weaker team holds much stronger team, FDR gap must be >= 2.0
      if (margin === 0) {
        const homePrev = prevMap[f.team_h];
        const awayPrev = prevMap[f.team_a];
        if (homePrev && awayPrev) {
          const homeFDR = homePrev.home_difficulty || 5;
          const awayFDR = awayPrev.away_difficulty || 5;
          const fdrGap = Math.abs(homeFDR - awayFDR);
          const compareScore = fdrGap - 2.0; // 2.0 handicap: draw must gap 2+ more than a win to beat it
          if (fdrGap >= 2.0 && (!biggestShock || compareScore > biggestShock.compareScore || (compareScore === biggestShock.compareScore && margin > biggestShock.margin))) {
            const weakerIsHome = homeFDR < awayFDR;
            const weakerTeam = getTeam(weakerIsHome ? f.team_h : f.team_a);
            const strongerTeam = getTeam(weakerIsHome ? f.team_a : f.team_h);
            const weakerFDR = weakerIsHome ? homeFDR : awayFDR;
            const strongerFDR = weakerIsHome ? awayFDR : homeFDR;
            biggestShock = {
              winner: weakerTeam.name,
              loser: strongerTeam.name,
              score: `${f.team_h_score}-${f.team_a_score}`,
              fdrGap,
              compareScore,
              margin: 0,
              winnerBadge: weakerTeam.badge,
              winnerFDR: weakerFDR.toFixed(1),
              loserFDR: strongerFDR.toFixed(1),
              weakerIsHome,
              isDraw: true
            };
          }
        }
      }
    });

    // Sort big wins by margin descending
    bigWins.sort((a, b) => b.margin - a.margin);
  }

  // --- Easiest Fixtures Ahead computation ---
  const nextGW = gwNumber ? parseInt(gwNumber) + 1 : null;
  const outlookData = (() => {
    if (!nextGW || !fixturesData || !teams || teams.length === 0) return null;

    const gwsRemaining = Math.max(0, 38 - nextGW + 1);
    const horizons = gwsRemaining >= 6
      ? [
          { label: 'Next GW', gws: 1 },
          { label: 'Next 3 GWs', gws: 3 },
          { label: 'Next 6 GWs', gws: 6 }
        ]
      : [
          { label: 'Next GW', gws: 1 },
          { label: 'Next 2 GWs', gws: 2 },
          { label: 'Next 3 GWs', gws: 3 }
        ];

    // Build a lookup: teams by id
    const teamById = {};
    teams.forEach(t => { teamById[t.id] = t; });

    return horizons.map(({ label, gws }) => {
      const startGW = nextGW;
      const endGW = Math.min(nextGW + gws - 1, 38);
      const gwRange = [];
      for (let gw = startGW; gw <= endGW; gw++) gwRange.push(gw);

      const teamRows = teams.filter(t => t.id > 0).map(team => {
        // Build per-GW fixture info
        const gwFixtures = gwRange.map(gw => {
          const fixtures = fixturesData.filter(
            f => f.event === gw && (f.team_h === team.id || f.team_a === team.id)
          );
          if (fixtures.length === 0) return [{ blank: true }];
          return fixtures.map(f => {
            const isHome = f.team_h === team.id;
            const opponentId = isHome ? f.team_a : f.team_h;
            const opponent = teamById[opponentId];
            const overall = opponent ? (isHome ? (opponent.a_diff || 5) : (opponent.h_diff || 5)) : 5;
            const att = opponent ? (isHome ? (opponent.a_def || 5) : (opponent.h_def || 5)) : 5;
            const def = opponent ? (isHome ? (opponent.a_att || 5) : (opponent.h_att || 5)) : 5;
            return {
              opponentInitial: opponent ? opponent.initial : '???',
              isHome,
              overall,
              att,
              def
            };
          });
        });

        // Compute reversed custom difficulty (matches calendar logic)
        // Sum raw difficulties, count extra fixtures (DGWs), then reverse
        let totalOverall = 0, totalAtt = 0, totalDef = 0;
        let extraFixtures = 0;
        gwRange.forEach((gw, i) => {
          const fixs = gwFixtures[i];
          if (fixs[0] && fixs[0].blank) {
            totalOverall += 11; totalAtt += 11; totalDef += 11;
          } else {
            if (fixs.length > 1) extraFixtures += fixs.length - 1;
            fixs.forEach(f => {
              totalOverall += f.overall;
              totalAtt += f.att;
              totalDef += f.def;
            });
          }
        });
        const numGWs = gwRange.length;
        const maxScore = (numGWs + extraFixtures) * 11;
        const revOverall = maxScore - totalOverall;
        const revAtt = maxScore - totalAtt;
        const revDef = maxScore - totalDef;

        return { team, gwFixtures, revOverall, revAtt, revDef };
      });

      // Sort by reversed overall descending (highest = easiest)
      teamRows.sort((a, b) => b.revOverall - a.revOverall);
      return { label, gws, actualGWs: gwRange.length, gwRange, top5: teamRows.slice(0, 5) };
    });
  })();

  return (
    <section className={isLight ? 'section-light' : 'section-white'}>
      <div className="fdr-wrapper">
        <div className="fdr-heading">
          <div className="recap-title-row">
            <h2>{title}</h2>

            {stillLive && !isMidSeason && <span className="recap-live-badge">LIVE</span>}
            <span className="recap-date">{dateDisplay}</span>
          </div>
          <p>{description}</p>

          {!isMidSeason && (
            <p className="recap-shock">
              <strong>Shock of the week:</strong>{' '}
              {biggestShock
                ? (biggestShock.isDraw
                    ? `${biggestShock.winner} (${biggestShock.winnerFDR}) managed a draw ${biggestShock.weakerIsHome ? 'at home' : 'away'} against ${biggestShock.loser} (${biggestShock.loserFDR}) ${biggestShock.score}`
                    : `${biggestShock.winner} (${biggestShock.winnerFDR}) won ${biggestShock.homeWon ? 'at home' : 'away'} against ${biggestShock.loser} (${biggestShock.loserFDR}) ${biggestShock.score}`)
                : (stillLive ? 'There have been no major shocks so far this GW' : 'There were no major shocks this GW')
              }
            </p>
          )}

          {!isMidSeason && (
            <p className="recap-big-wins">
              <strong>Big win{bigWins.length !== 1 ? 's' : ''}:</strong>{' '}
              {bigWins.length > 0
                ? bigWins.map((w, i) => (
                    <span key={i}>
                      {w.winner} {w.score} {w.loser}{i < bigWins.length - 1 ? ', ' : ''}
                    </span>
                  ))
                : (stillLive ? 'There have been no big wins so far this GW' : 'There were no big wins this GW')
              }
            </p>
          )}

          <div className="movers-container">
            {risers.length > 0 && (
              <div className="movers-section">
                <h3 className="movers-section-title movers-title-up">Biggest Risers</h3>
                <div className="movers-row">
                  {risers.map(m => renderMoverCard(m, 'up'))}
                </div>
              </div>
            )}
            {fallers.length > 0 && (
              <div className="movers-section">
                <h3 className="movers-section-title movers-title-down">Biggest Fallers</h3>
                <div className="movers-row">
                  {fallers.map(m => renderMoverCard(m, 'down'))}
                </div>
              </div>
            )}

            {outlookData && (
              <div className="outlook-section">
                <h3 className="outlook-title">Best Upcoming Fixtures</h3>
                <div className="outlook-row">
                  {outlookData.filter(({ actualGWs }, i, arr) => {
                    // Hide a grid if it covers the same number of actual GWs as the previous one
                    // (e.g. "Next 6 GWs" when only 1 GW remains shows same data as "Next GW")
                    if (actualGWs === 0) return false;
                    if (i === 0) return true;
                    return actualGWs > arr[i - 1].actualGWs;
                  }).map(({ label, actualGWs, top5 }) => (
                    <div key={label} className="outlook-table-wrapper">
                      <table className="outlook-table">
                        <thead>
                          <tr>
                            <th className="outlook-th-team">{label}</th>
                            <th className="outlook-th-avg">OVR</th>
                            <th className="outlook-th-avg">ATT</th>
                            <th className="outlook-th-avg">DEF</th>
                          </tr>
                        </thead>
                        <tbody>
                          {top5.map(({ team, revOverall, revAtt, revDef }) => {
                            // Normalise by actual GWs available (not requested horizon)
                            // so end-of-season grids aren't falsely red/orange
                            const colorScale = (val) => getDifficultyColor(11 - val / actualGWs);
                            return (
                            <tr key={team.id}>
                              <td className="outlook-td-team">
                                {team.badge && (
                                  <img src={team.badge} alt={team.initial} className="outlook-badge" />
                                )}
                                <span className="outlook-name">{team.name}</span>
                                <span className="outlook-initial">{team.initial}</span>
                              </td>
                              <td className="outlook-td-avg">
                                <span className="outlook-avg-pill" style={{ backgroundColor: colorScale(revOverall) }}>
                                  {revOverall.toFixed(1)}
                                </span>
                              </td>
                              <td className="outlook-td-avg">
                                <span className="outlook-avg-pill" style={{ backgroundColor: colorScale(revAtt) }}>
                                  {revAtt.toFixed(1)}
                                </span>
                              </td>
                              <td className="outlook-td-avg">
                                <span className="outlook-avg-pill" style={{ backgroundColor: colorScale(revDef) }}>
                                  {revDef.toFixed(1)}
                                </span>
                              </td>
                            </tr>
                          );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GWRecapPost;
